import { ORDER_STATUSES, type OrderStatus } from "./order-types";
import { dateOnlyKey } from "../values/date-only";
import {
  EXPECTED_DELIVERY_LABEL,
  LIFECYCLE_DEFAULT_DATE_LABELS,
  lifecycleFutureMessage,
  lifecyclePrecedesMessage,
  lifecycleRequiredMessage,
  lifecycleRequiresLaterStatusMessage,
} from "./order-policy-copy";

export const LIFECYCLE_DATE_FIELDS = ["submittedAt", "orderedAt", "arrivedDate"] as const;
export type LifecycleDateField = (typeof LIFECYCLE_DATE_FIELDS)[number];

export type LifecycleDates = {
  status: OrderStatus;
  submittedAt: Date | null;
  orderedAt: Date | null;
  arrivedDate: Date | null;
};

export type LifecycleDateIssue = { field: LifecycleDateField; message: string };

export type LifecycleDateLabels = Partial<Record<LifecycleDateField, string>>;

const DIRECTION_NEUTRAL_LABELS: Record<LifecycleDateField, string> = LIFECYCLE_DEFAULT_DATE_LABELS;

export function isLifecycleDateRequired(field: LifecycleDateField, status: OrderStatus): boolean {
  if (field === "submittedAt") return true;
  if (field === "arrivedDate") return status === ORDER_STATUSES.fulfilled;
  return false;
}

function isLifecycleDateAllowed(field: LifecycleDateField, status: OrderStatus): boolean {
  if (field === "submittedAt") return true;
  if (field === "orderedAt") return status !== ORDER_STATUSES.requested;
  return status === ORDER_STATUSES.fulfilled;
}

function label(field: LifecycleDateField, labels?: LifecycleDateLabels): string {
  return labels?.[field] ?? DIRECTION_NEUTRAL_LABELS[field];
}

export function lifecycleDateIssues(
  dates: LifecycleDates,
  now: Date = new Date(),
  labels?: LifecycleDateLabels,
): LifecycleDateIssue[] {
  const issues: LifecycleDateIssue[] = [];
  const { status } = dates;
  const add = (field: LifecycleDateField, message: string) => issues.push({ field, message });

  for (const field of LIFECYCLE_DATE_FIELDS) {
    const value = dates[field];
    if (!value) {
      if (isLifecycleDateRequired(field, status)) add(field, lifecycleRequiredMessage(label(field, labels)));
      continue;
    }
    if (!isLifecycleDateAllowed(field, status)) {
      add(field, lifecycleRequiresLaterStatusMessage(label(field, labels)));
      continue;
    }
    if (dateOnlyKey(value) > dateOnlyKey(now)) add(field, lifecycleFutureMessage(label(field, labels)));
  }

  const { submittedAt, orderedAt, arrivedDate } = dates;
  if (submittedAt && orderedAt && orderedAt < submittedAt) {
    add("orderedAt", lifecyclePrecedesMessage(label("orderedAt", labels), label("submittedAt", labels)));
  }
  if (orderedAt && arrivedDate && arrivedDate < orderedAt) {
    add("arrivedDate", lifecyclePrecedesMessage(label("arrivedDate", labels), label("orderedAt", labels)));
  }
  if (submittedAt && arrivedDate && arrivedDate < submittedAt) {
    add("arrivedDate", lifecyclePrecedesMessage(label("arrivedDate", labels), label("submittedAt", labels)));
  }

  return issues;
}

export function expectedDeliveryDateIssues(
  submittedAt: Date | null | undefined,
  expectedDeliveryDate: Date | null | undefined,
  labels?: LifecycleDateLabels & { expectedDeliveryDate?: string },
): { field: "expectedDeliveryDate"; message: string }[] {
  if (submittedAt && expectedDeliveryDate &&
    dateOnlyKey(expectedDeliveryDate) < dateOnlyKey(submittedAt)) {
    return [{
      field: "expectedDeliveryDate",
      message: lifecyclePrecedesMessage(labels?.expectedDeliveryDate ?? EXPECTED_DELIVERY_LABEL, label("submittedAt", labels)),
    }];
  }
  return [];
}

